import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { trackSegment } from '~/components/SegmentAnalytics';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { Text } from '~/eds/src/components/Text/Text';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { api } from '~/redux/api/createApi';
import { UserAttributes } from '~/redux/api/methods/roles/roles';
import { ParsedType } from '~/redux/api/transformers/utils';
import { coerceRtkqError } from '~/redux/api/utils';
import { Nullable } from '~/types/Nullable';

type Props = {
  user: Nullable<ParsedType<UserAttributes>>;
  visible: boolean;
  onHide: () => void;
  isWorkdayUser?: boolean;
};

export const ResetUserPasswordModal = ({
  user,
  visible,
  onHide,
  isWorkdayUser = false,
}: Props) => {
  const intl = useIntl();
  const isRolodexDataSourceEnabled = useFlag(FlagType.RolodexAsUserDataSource);

  const [sendInvitationEmail, sendInvitationEmailResult] =
    api.endpoints.sendInvitationEmail.useMutation();
  const { toast } = useToast();
  const { isError, isLoading, isSuccess, error } = sendInvitationEmailResult;
  const currentUser = useCurrentUser();

  useEffect(() => {
    if (isSuccess) {
      toast({
        message: intl.formatMessage(
          {
            id: 'ResetUserPasswordModal.successMessage',
            defaultMessage: 'We have sent a reset password link to "{email}"',
            description:
              'Success message when password reset email is sent. Example: email = "user@example.com"',
          },
          { email: user?.email },
        ),
        status: 'success',
      });
      onHide();
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) {
      const resp = coerceRtkqError(error)?.response || {};
      const msg = resp.data?.errors?.[0];
      toast({
        message:
          msg?.title ||
          intl.formatMessage({
            id: 'ResetUserPasswordModal.errorMessage',
            defaultMessage: 'An error occurred while sending invitation email.',
            description: 'Error message when sending invitation email fails',
          }),
        status: 'danger',
      });
    }
  }, [isError]);

  return (
    <Modal
      isVisible={visible}
      title={intl.formatMessage({
        id: 'ResetUserPasswordModal.title',
        defaultMessage: 'Reset password',
        description: 'Modal title for password reset',
      })}
      onCancel={onHide}
      onHide={onHide}
      primaryAction={{
        text: intl.formatMessage({
          id: 'ResetUserPasswordModal.resetButton',
          defaultMessage: 'Reset Password',
          description: 'Button text for password reset',
        }),
        onClick: () => {
          trackSegment('submitResetUserPassword', {
            id: user!.id,
            user_id: currentUser.id,
            client_id: currentUser.client,
          });
          sendInvitationEmail({
            userId: user!.id,
            enableRolodex: isWorkdayUser || isRolodexDataSourceEnabled,
          });
        },
        isLoading: isLoading,
        variant: 'primary',
      }}
    >
      <Text>
        <FormattedMessage
          id="ResetUserPasswordModal.confirmationText"
          defaultMessage="User will receive an email with a password reset link at <strong>{email}</strong>"
          description="Confirmation message for password reset. Example: email = 'user@example.com'"
          values={{
            email: user?.email,
            strong: (chunks) => <Text variant="body-bold">{chunks}</Text>,
          }}
        />
      </Text>
    </Modal>
  );
};
